import { BindingEngine, observable } from 'aurelia-binding';
import { BalanceReportViewModel, BalanceTotalsQueryModel, ClickHouseReportDateRange, ClickHouseReportDateRangeInterval, GlReportsClient, PortfolioReportClient } from '@dynamik/common/clients/reporting-clients';
import { CacheService, CustomerService, IApplicationFilter, NumericFormatterValueConverter, property, when, widget } from '@dynamik/common';
import { WidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';
import { ColumnState, GridApi, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { ReplaySubject } from '../../../rxjs-local';
import { ThemeService } from '../../../services/theme-service';
import { Size } from '@aurelia-mdc-web/base';
import { addDays, format, startOfYear } from '../../../date-fns-local';

interface IBalanceSheetRow {
  name: string;
  period: number;
  ytd: number;
  isTotal?: boolean;
}

@customElement('profit-loss-widget')
@widget('P&L', false, true, 'Profit and loss widget')
export class ProfitLossWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private glReportsClient: GlReportsClient, bindingEngine: BindingEngine, private cacheService: CacheService,
    private numericFormatter: NumericFormatterValueConverter, public themeService: ThemeService, private portfolioReportClient: PortfolioReportClient) {
    super(element, bindingEngine, customerService);
    this.gridReady$ = new ReplaySubject<void>(1);
  }

  gridApi: GridApi<IBalanceSheetRow>;
  suppressSaveColumnState: boolean;
  gridOptions: GridOptions<IBalanceSheetRow>;
  data: BalanceReportViewModel;
  moneyFormatter = (params: ValueFormatterParams) => this.numericFormatter.toView(params.value as number, 0);

  @property(null)
  columnStates?: ColumnState[];

  @property(null)
  filterModel?: ReturnType<GridApi['getFilterModel']>;

  @property('Table')
  fitColumns: boolean;

  @property('Table')
  fontSize?: number = 8;

  @property('Table')
  rowHeight?: number = 20;

  @observable
  size: Size;
  sizeChanged() {
    if (this.gridApi && this.fitColumns) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  override activate(config: unknown) {
    super.activate(config)
    this.gridOptions = {
      defaultColDef: {
        sortable: false,
        resizable: true,
        cellStyle: x => ({
          'border-top': x.data.isTotal ? `1px solid ${this.themeService.getChartLabelColour(undefined)}` : 'none',
          'border-bottom': x.data.isTotal ? `1px solid ${this.themeService.getChartLabelColour(undefined)}` : 'none'
        }),
      },
      popupParent: document.body,
      rowHeight: this.rowHeight,
      rowModelType: 'clientSide',
      rowSelection: { mode: 'singleRow', checkboxes: false },
      animateRows: false,
      scrollbarWidth: 11,
      onColumnMoved: () => this.saveColumnState(),
      onColumnVisible: () => this.saveColumnState(),
      onColumnResized: () => this.saveColumnState(),
      onFilterChanged: () => this.saveColumnState(),
      onSortChanged: () => this.saveColumnState(),
      onDisplayedColumnsChanged: params => {
        if (params.source !== 'gridInitializing') {
          this.saveColumnState();
        }
      },
      getRowId: x => x.data.name,
      onGridReady: params => {
        this.gridApi = params.api;
        this.gridReady$.next();
      },
      onFirstDataRendered: () => {
        this.suppressSaveColumnState = true;
        setTimeout(() => this.suppressSaveColumnState = false, 2000);
        if (this.fitColumns) {
          this.gridApi.sizeColumnsToFit();
        } else {
          if (this.columnStates?.length) {
            this.gridApi.applyColumnState({ state: this.columnStates, applyOrder: true });
          }
        }
        if (this.filterModel?.length) {
          this.gridApi.setFilterModel(this.filterModel);
        }
      }
    };
  }

  saveColumnState() {
    if (this.suppressSaveColumnState) {
      return;
    }
    this.columnStates = this.gridApi.getColumnState();
    this.filterModel = this.gridApi.getFilterModel();
    this.element.dispatchEvent(new CustomEvent('widget-change', { bubbles: true }));
  }

  notifyLoading() {
    this.loading = true;
    this.gridApi.setGridOption('loading', true);
  }

  notifyLoaded() {
    this.loading = false;
    this.gridApi.setGridOption('loading', false);
  }

  protected override loadData(filter: IApplicationFilter): Promise<BalanceTotalsQueryModel[]> {
    const prevYearEndStr = format(addDays(startOfYear(filter.dates.to), -1), 'yyyy-MM-dd');
    const dayBeforeFromStr = format(addDays(filter.dates.from, -1), 'yyyy-MM-dd');
    return this.portfolioReportClient.balance(filter.customer.companyId, [
      new ClickHouseReportDateRange({ name: "start", from: dayBeforeFromStr, to: dayBeforeFromStr, interval: ClickHouseReportDateRangeInterval.Day }),
      new ClickHouseReportDateRange({ name: "end", from: filter.dates.toStr, to: filter.dates.toStr, interval: ClickHouseReportDateRangeInterval.Day }),
      ...when(prevYearEndStr !== dayBeforeFromStr, new ClickHouseReportDateRange({ name: "prevYearEnd", from: prevYearEndStr, to: prevYearEndStr, interval: ClickHouseReportDateRangeInterval.Day })),
    ]);
  }

  protected override async setData(data: BalanceTotalsQueryModel[]): Promise<void> {
    const start = data.find(x => x.periodName === 'start');
    const end = data.find(x => x.periodName === 'end');
    const yearStart = data.find(x => x.periodName === 'prevYearEnd') ?? start;

    // Calculate period and YTD values
    const p = new BalanceTotalsQueryModel();
    for (const key of Object.keys(end)) {
      if (typeof end[key] === 'number') {
        p[key] = (end[key] as number) - (start[key] as number);
      }
    }

    const y = new BalanceTotalsQueryModel();
    for (const key of Object.keys(end)) {
      if (typeof end[key] === 'number') {
        y[key] = (end[key] as number) - (yearStart[key] as number);
      }
    }

    const pOperatingIncome = p.glRevenues + p.glCostOfGoodsSold;
    const yOperatingIncome = y.glRevenues + y.glCostOfGoodsSold;
    const pOperatingProfit = pOperatingIncome + p.glPersonellExpenses + p.glOtherOperatingExpenses;
    const yOperatingProfit = yOperatingIncome + y.glPersonellExpenses + y.glOtherOperatingExpenses;
    const pNetFinancialItems = p.glFinancialIncomeAndExpenses + p.unrealizedProfitLoss;
    const yNetFinancialItems = y.glFinancialIncomeAndExpenses + y.unrealizedProfitLoss;
     const rowData = [
       { name: 'Operating income', period: pOperatingIncome, ytd: yOperatingIncome },
       { name: 'Personell expenses', period: p.glPersonellExpenses, ytd: y.glPersonellExpenses },
       { name: 'Other operating expenses', period: p.glOtherOperatingExpenses, ytd: y.glOtherOperatingExpenses },
       { name: 'Operating profit', period: pOperatingProfit, ytd: yOperatingProfit, isTotal: true },
       { name: 'Net Interest', period: p.glNetInterest, ytd: y.glNetInterest },
       { name: 'Change In Value', period: p.unrealizedProfitLoss, ytd: y.unrealizedProfitLoss },
       { name: 'Net sold securities', period: p.glNetSoldSecurities, ytd: y.glNetSoldSecurities },
       { name: 'Dividend', period: p.glDividend, ytd: y.glDividend },
       { name: 'Net Currency', period: p.glNetCurrency, ytd: y.glNetCurrency },
       { name: 'Other financial items', period: p.glOtherFinancialItems, ytd: y.glOtherFinancialItems },
       { name: 'Net financial items', period: pNetFinancialItems, ytd: yNetFinancialItems, isTotal: true },
       { name: 'Net income (before tax)', period: pOperatingProfit + pNetFinancialItems, ytd: yOperatingProfit + yNetFinancialItems },
     ];
     this.gridApi.setGridOption('rowData', rowData);
     this.excelData = {
       fileName: 'Profit Loss',
       data: { Items: rowData }
     };
     this.gridApi.getColumn('ytd').getColDef().headerName = `YTD ${format(end.date, 'dd.MM.yyyy')}`;
     this.gridApi.refreshHeader();
  }

  override configChanged(key: keyof ProfitLossWidget) {
    switch (key) {
      case 'fontSize':
        break;
      case 'rowHeight':
        this.rowHeightChanged();
        break;
      default:
        this.refresh$.next(undefined);
        break;
    }
  }

  rowHeightChanged() {
    this.gridApi.setGridOption('rowHeight', this.rowHeight);
    this.gridApi.onRowHeightChanged();
  }
}
